import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Layout } from "lucide-react";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "", brokerage_name: "", header_text: "", footer_text: "",
    disclaimers: "", signature_block: "", accent_color: "#C5A059",
  });

  const load = async () => {
    const { data } = await api.get("/templates");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    await api.post("/templates", form);
    toast.success("Template saved");
    setShowNew(false);
    setForm({ name: "", brokerage_name: "", header_text: "", footer_text: "", disclaimers: "", signature_block: "", accent_color: "#C5A059" });
    load();
  };

  const del = async (id) => {
    if (!confirm("Delete template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="templates-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="kicker mb-2">Branding</div>
          <h1 className="text-4xl font-display">Templates</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-lg">
            Save brokerage letterheads, disclaimers, and signature blocks to apply on every offer.
          </p>
        </div>
        <button className="btn-brass" onClick={() => setShowNew(!showNew)} data-testid="new-template-toggle">
          <Plus size={16} /> New Template
        </button>
      </div>

      {showNew && (
        <form onSubmit={save} className="grid-panel p-6 mb-6 space-y-4" data-testid="template-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TInput label="Template Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="tpl-name" />
            <TInput label="Brokerage Name" value={form.brokerage_name} onChange={(v) => setForm({ ...form, brokerage_name: v })} testid="tpl-brokerage" />
          </div>
          <TArea label="Letterhead / Header Text" value={form.header_text} onChange={(v) => setForm({ ...form, header_text: v })} testid="tpl-header" />
          <TArea label="Footer / Disclaimer" value={form.footer_text} onChange={(v) => setForm({ ...form, footer_text: v })} testid="tpl-footer" />
          <TArea label="Signature Block" value={form.signature_block} onChange={(v) => setForm({ ...form, signature_block: v })} testid="tpl-sig" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Accent Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="h-10 w-16 rounded-sm border border-border" data-testid="tpl-color" />
                <input type="text" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="flex-1 bg-white border border-border px-3 py-2.5 text-sm rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-brass" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            <button type="submit" className="btn-brass" data-testid="tpl-save">Save Template</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 ? (
          <div className="col-span-2 grid-panel p-10 text-center">
            <Layout size={28} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">No templates yet. The default OfferForge template will be used on documents.</div>
          </div>
        ) : items.map((t) => (
          <div key={t.id} className="grid-panel p-5" data-testid={`tpl-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="kicker">Template</div>
                <div className="font-display text-xl mt-1">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.brokerage_name || "—"}</div>
              </div>
              <button onClick={() => del(t.id)} className="text-slate-400 hover:text-red-600" data-testid={`tpl-del-${t.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
            <div className="mt-4 h-3 rounded-sm" style={{ background: t.accent_color || "#C5A059" }}></div>
            {t.header_text && <div className="mt-3 text-xs text-slate-600 line-clamp-2">{t.header_text}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function TInput({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{label}</label>
      <input type="text" required={required} value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
    </div>
  );
}

function TArea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{label}</label>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
    </div>
  );
}
